import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import { useLanguage } from '../contexts/LanguageContext';
import { translateCategory } from '../utils/meetingLocale';

export interface CategoryOption {
  key: string; // raw value as stored in meetings_sessions.meeting_category
  label?: string; // optional pre-translated label
}

interface CategoryFilterContentProps {
  onSelectionChange: (selectedCategories: string[]) => void;
  currentSelection: string[];
  // List of distinct categories present in the loaded meetings_sessions. When
  // omitted the component renders an empty list — callers must pass the
  // session-derived options to keep the filter aligned with available data.
  options?: CategoryOption[];
}

export default function CategoryFilterContent({ onSelectionChange, currentSelection, options: providedOptions }: CategoryFilterContentProps) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState<string | null>(currentSelection[0] || null);

  const options = (providedOptions || []).map((opt) => ({
    key: opt.key,
    label: opt.label || translateCategory(opt.key, t),
  }));

  const handleOptionPress = (key: string) => {
    setSelected(selected === key ? null : key);
  };

  useEffect(() => {
    onSelectionChange(selected ? [selected] : []);
  }, [selected, onSelectionChange]);

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {options.map((opt) => (
        <TouchableOpacity
          key={opt.key}
          style={[styles.optionCard, selected === opt.key && styles.selectedOptionCard]}
          onPress={() => handleOptionPress(opt.key)}
        >
          <Text style={styles.optionText}>{opt.label}</Text>
          <View style={[styles.checkbox, selected === opt.key && styles.selectedCheckbox]}>
            {selected === opt.key && (
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path
                  d="M12.6654 4.7915L6.2487 11.2082L3.33203 8.2915"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </Svg>
            )}
          </View>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    height: (68 * 5) + (8 * 4),
  },
  optionCard: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#F9F8F3',
    gap: 4,
    marginBottom: 8,
  },
  selectedOptionCard: {
    borderColor: '#004165',
    backgroundColor: '#E6EBF0',
  },
  optionText: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    lineHeight: 19.6,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#7E7D75',
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedCheckbox: {
    borderWidth: 3,
    borderColor: '#015480',
    backgroundColor: '#015480',
    padding: 4,
  },
});
